import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatDate } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { MagnifyingGlass, ArrowClockwise, House, CalendarBlank, Warning, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { roomImage } from "@/lib/roomImages";

const todayIso = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, d) => { const dt = new Date(iso); dt.setDate(dt.getDate()+d); return dt.toISOString().slice(0,10); };
const nightsBetween = (ci, co) => {
  if (!ci || !co) return 0;
  const d = (new Date(co) - new Date(ci)) / 86400000;
  return d > 0 ? Math.round(d) : 0;
};

export default function Rooms() {
  const nav = useNavigate();
  const [rooms, setRooms]     = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchCheckIn,  setSearchCheckIn]  = useState(todayIso());
  const [searchCheckOut, setSearchCheckOut] = useState(addDays(todayIso(), 1));
  const [searchGuests,   setSearchGuests]   = useState(1);
  const [searchRoomsNeeded, setSearchRoomsNeeded] = useState(1);
  const [livingroomExtra, setLivingroomExtra] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [searching, setSearching]       = useState(false);
  const [selected, setSelected] = useState([]); // Zimmer-IDs für Mehrfach-Reservierung

  const nights = nightsBetween(searchCheckIn, searchCheckOut);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { setRooms((await api.get("/rooms")).data); }
      finally { setLoading(false); }
    })();
  }, []);

  // Wenn Abreise vor Anreise liegt → auf Anreise+1 setzen
  useEffect(() => {
    if (searchCheckIn && searchCheckOut && new Date(searchCheckOut) <= new Date(searchCheckIn)) {
      setSearchCheckOut(addDays(searchCheckIn, 1));
    }
  }, [searchCheckIn, searchCheckOut]);

  const search = async () => {
    if (!searchCheckIn || !searchCheckOut) { toast.error("Bitte An- und Abreise wählen"); return; }
    if (nights <= 0) { toast.error("Abreise muss nach Anreise liegen"); return; }
    const g = Math.max(1, Math.min(10, Number(searchGuests) || 1));
    const rn = Math.max(1, Math.min(4, Number(searchRoomsNeeded) || 1));
    setSearching(true);
    setSelected([]);
    try {
      const { data } = await api.get(`/rooms/availability`, { params: {
        check_in: searchCheckIn, check_out: searchCheckOut, guests: g,
        rooms_needed: rn,
        livingroom_extra: livingroomExtra,
      }});
      setAvailability({ ...data, guests: g, rooms_needed: rn, livingroom_extra: livingroomExtra });
      const free = data.rooms.filter(r => r.available).length;
      const totalFreeCap = data.rooms.filter(r => r.available).reduce((s, r) => s + (r.max_guests || 0), 0);
      if (free === 0) {
        toast.error("Kein Zimmer im gewählten Zeitraum frei");
      } else if (data.must_split) {
        toast.info(`Gruppe von ${g} Personen wird auf mehrere Zimmer verteilt · ${free} Zimmer frei (Kapazität: ${totalFreeCap})`);
      } else if (free < rn) {
        toast.info(`${free} von ${rn} gesuchten Zimmern frei · alle Optionen werden angezeigt`);
      } else {
        toast.success(`${free} passende Zimmer verfügbar`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verfügbarkeit konnte nicht geladen werden");
    } finally { setSearching(false); }
  };

  const clearSearch = () => {
    setAvailability(null); setSearchGuests(1); setSearchRoomsNeeded(1); setSelected([]);
    setSearchCheckIn(todayIso()); setSearchCheckOut(addDays(todayIso(), 1));
    setLivingroomExtra(false);
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      const rn = availability?.rooms_needed || 1;
      const mustSplit = !!availability?.must_split;
      const matchingCount = (availability?.rooms || []).filter(a => a.available).length;
      // Beim Split-Modus keine Obergrenze außer „alle verfügbaren"
      const maxSelectable = mustSplit ? matchingCount : Math.min(rn, matchingCount);
      if (prev.length >= maxSelectable) {
        toast.info(`Maximal ${maxSelectable} Zimmer wählbar`);
        return prev;
      }
      return [...prev, id];
    });
  };

  const reserveSelected = () => {
    if (selected.length === 0) {
      toast.error("Bitte mindestens ein Zimmer auswählen");
      return;
    }
    const params = new URLSearchParams({
      new: "1",
      rooms: selected.join(","),
      check_in: availability.check_in,
      check_out: availability.check_out,
      guests: String(availability.guests || 1),
    });
    nav(`/buchungen?${params.toString()}`);
  };

  return (
    <div className="space-y-6" data-testid="rooms-page">
      <div>
        <h1 className="text-3xl font-bold" style={{ fontFamily: "'Work Sans',sans-serif" }}>Zimmer & Einheiten</h1>
        <p className="text-muted-foreground mt-1">
          {rooms.length} Einheiten · Status wird automatisch aus den Buchungen ermittelt · Klick auf eine Karte öffnet das gefilterte Monats-Journal.
        </p>
      </div>

      <section className="msb-card border-l-4 border-l-primary" data-testid="availability-search">
        <div className="flex items-center gap-3 mb-3">
          <CalendarBlank size={20} weight="fill" className="text-primary" />
          <div>
            <h2 className="font-bold text-lg" style={{ fontFamily: "'Work Sans',sans-serif" }}>Verfügbarkeit prüfen</h2>
            <p className="text-xs text-muted-foreground">An- und Abreise wählen · nur passende, freie Zimmer werden angezeigt.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Anreise</Label>
            <Input type="date" value={searchCheckIn} onChange={e => setSearchCheckIn(e.target.value)}
                   className="rounded-xl mt-1.5 w-44" data-testid="availability-checkin" />
          </div>
          <div>
            <Label className="text-xs">Abreise</Label>
            <Input type="date" value={searchCheckOut} min={searchCheckIn ? addDays(searchCheckIn, 1) : undefined}
                   onChange={e => setSearchCheckOut(e.target.value)}
                   className="rounded-xl mt-1.5 w-44" data-testid="availability-checkout" />
          </div>
          <div>
            <Label className="text-xs">Nächte</Label>
            <div className="mt-1.5 h-10 px-3 flex items-center rounded-xl bg-muted/60 border border-border w-20 text-sm font-semibold"
                 data-testid="availability-nights-display">
              {nights || "—"}
            </div>
          </div>
          <div>
            <Label className="text-xs">Personen</Label>
            <Input type="number" min="1" max="10" value={searchGuests} onChange={e => setSearchGuests(+e.target.value || 1)}
                   className="rounded-xl mt-1.5 w-24" data-testid="availability-guests" />
          </div>
          <div>
            <Label className="text-xs">Zimmer</Label>
            <Input type="number" min="1" max="4" value={searchRoomsNeeded} onChange={e => setSearchRoomsNeeded(+e.target.value || 1)}
                   className="rounded-xl mt-1.5 w-24" data-testid="availability-rooms-needed" />
          </div>
          <Button onClick={search} disabled={searching} className="rounded-xl bg-primary" data-testid="availability-search-btn">
            <MagnifyingGlass size={16} weight="bold" />
            <span className="ml-1.5">{searching ? "Suche…" : "Verfügbar zeigen"}</span>
          </Button>
          {availability && (
            <Button variant="outline" onClick={clearSearch} className="rounded-xl" data-testid="availability-clear-btn">
              <ArrowClockwise size={16} weight="bold" />
              <span className="ml-1.5">Filter zurücksetzen</span>
            </Button>
          )}
        </div>
        {/* Ausnahme-Toggle: Wohnzimmer bei 2 Personen freischalten */}
        {searchGuests >= 2 && (
          <label className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 cursor-pointer select-none"
                 data-testid="livingroom-extra-toggle">
            <input type="checkbox" checked={livingroomExtra}
                   onChange={e => setLivingroomExtra(e.target.checked)}
                   className="mt-0.5 h-4 w-4 accent-amber-600"/>
            <div className="text-sm">
              <div className="font-semibold text-amber-900">Wohnzimmer für 2 Personen freischalten?</div>
              <div className="text-xs text-amber-800/80">
                Ausnahme für Gruppenanfragen: Wohnzimmer hat regulär Platz für 1 Person, kann aber im Einzelfall 2 Personen aufnehmen.
              </div>
            </div>
          </label>
        )}
        {availability && (
          <div className="text-sm text-muted-foreground mt-3">
            Zeitraum: <strong>{formatDate(availability.check_in)}</strong> → <strong>{formatDate(availability.check_out)}</strong> · {availability.nights} {availability.nights === 1 ? "Nacht" : "Nächte"} · <strong>{availability.guests} {availability.guests === 1 ? "Person" : "Personen"}</strong> · <strong>{availability.rooms_needed} Zimmer</strong> gesucht
            {availability.livingroom_extra && <span className="ml-1 text-amber-800 font-semibold">· Wohnzimmer als Ausnahme aktiviert</span>}
          </div>
        )}
      </section>

      {loading ? (
        <div className="text-muted-foreground text-sm">Lade Zimmer…</div>
      ) : availability ? (() => {
        const matching = availability.rooms.filter(a => a.available);
        const rn = availability.rooms_needed || 1;
        if (availability.rooms.length === 0) {
          return (
            <div className="msb-card text-center py-10" data-testid="no-matching-rooms">
              <Warning size={32} weight="duotone" className="mx-auto text-amber-500 mb-2"/>
              <div className="font-semibold">Kein Zimmer passt zu dieser Personenzahl</div>
              <p className="text-sm text-muted-foreground mt-1">Bitte Personenanzahl reduzieren oder Filter zurücksetzen.</p>
            </div>
          );
        }
        if (matching.length === 0) {
          return (
            <div className="msb-card text-center py-10" data-testid="no-available-rooms">
              <Warning size={32} weight="duotone" className="mx-auto text-amber-500 mb-2"/>
              <div className="font-semibold">Kein passendes Zimmer für diesen Zeitraum verfügbar</div>
              <p className="text-sm text-muted-foreground mt-1">Bitte anderen Zeitraum wählen oder Filter zurücksetzen.</p>
            </div>
          );
        }
        const shortfall = rn > 1 && matching.length < rn;
        const mustSplit = !!availability.must_split;
        // Kapazitäts-Summe der aktuell gewählten Zimmer (für den Hinweis)
        const capacitySum = selected
          .map(id => rooms.find(r => r.id === id))
          .reduce((sum, r) => sum + (r?.max_guests || 0), 0);
        const totalFreeCap = matching
          .map(a => rooms.find(r => r.id === a.id))
          .reduce((sum, r) => sum + (r?.max_guests || 0), 0);
        const capacityOk = capacitySum >= (availability.guests || 1);
        return (
          <>
            {mustSplit && (
              <div className="msb-card border-l-4 border-l-primary bg-primary/5 py-3" data-testid="must-split-hint">
                <div className="flex items-start gap-2">
                  <Warning size={18} weight="fill" className="text-primary mt-0.5"/>
                  <div className="text-sm">
                    <div className="font-semibold text-primary">
                      Gruppe von {availability.guests} Personen wird auf mehrere Zimmer verteilt
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Kein einzelnes Zimmer ist groß genug – wähle eine Kombination. Gesamt-Kapazität der freien Zimmer: <strong>{totalFreeCap} Personen</strong>.
                    </div>
                  </div>
                </div>
              </div>
            )}
            {shortfall && (
              <div className="msb-card border-l-4 border-l-amber-500 bg-amber-50/60 py-3" data-testid="shortfall-hint">
                <div className="flex items-start gap-2">
                  <Warning size={18} weight="fill" className="text-amber-600 mt-0.5"/>
                  <div className="text-sm">
                    <div className="font-semibold text-amber-900">
                      Nur {matching.length} von {rn} Zimmern verfügbar
                    </div>
                    <div className="text-xs text-amber-800/80">
                      Du kannst trotzdem einzelne Zimmer reservieren, die Personen frei aufteilen oder einen anderen Zeitraum wählen.
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-24">
              {matching.map(a => {
                const r = rooms.find(x => x.id === a.id);
                if (!r) return null;
                return <RoomCard key={r.id} room={r} availability={a} nav={nav}
                                 guests={availability.guests}
                                 checkOut={availability.check_out}
                                 selectable={rn > 1 || mustSplit}
                                 selected={selected.includes(r.id)}
                                 onToggle={() => toggleSelect(r.id)} />;
              })}
            </div>
            {(rn > 1 || mustSplit) && (
              <div className="fixed bottom-4 inset-x-4 md:left-72 md:right-8 msb-card border-2 border-primary shadow-2xl flex items-center justify-between gap-4 flex-wrap z-30"
                   data-testid="multi-reserve-bar">
                <div>
                  <div className="font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>
                    {selected.length} Zimmer ausgewählt {selected.length < rn ? `(gesucht: ${rn})` : ""}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {selected.length === 0 ? `Verfügbar: ${matching.length} · ` : (
                      <span className={capacityOk ? "text-emerald-700 font-semibold" : "text-amber-800 font-semibold"}>
                        Kapazität: {capacitySum} / {availability.guests || 1} Pers. · 
                      </span>
                    )} 
                    {formatDate(availability.check_in)} → {formatDate(availability.check_out)}
                  </div>
                </div>
                <Button onClick={reserveSelected} disabled={selected.length === 0}
                        className="rounded-xl bg-primary" data-testid="multi-reserve-btn">
                  <CheckCircle size={16} weight="fill"/>
                  <span className="ml-1.5">{selected.length || 0} Zimmer reservieren</span>
                </Button>
              </div>
            )}
          </>
        );
      })() : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rooms.map(r => (
            <RoomCard key={r.id} room={r} availability={null} nav={nav} guests={1}/>
          ))}
        </div>
      )}
    </div>
  );
}

function RoomCard({ room, availability, nav, guests, checkOut, selectable, selected, onToggle }) {
  const searchMode = !!availability;
  const isBusy = !!room.current_booking;
  const statusStyle = isBusy
    ? { bg: "bg-red-600 text-white ring-1 ring-red-700 shadow-md", label: "Belegt" }
    : { bg: "bg-emerald-100 text-emerald-800",                     label: "Frei"   };

  let overlay = null;
  if (availability && availability.available) {
    overlay = (
      <div className={`absolute inset-0 border-[3px] ${selected ? "border-primary" : "border-emerald-500"} rounded-2xl pointer-events-none flex items-start justify-end p-3`}>
        <div className={`${selected ? "bg-primary" : "bg-emerald-600"} text-white px-3 py-1 rounded-full flex items-center gap-1.5 shadow-md`}>
          <CheckCircle size={14} weight="fill" />
          <span className="font-bold text-xs">{selected ? "Ausgewählt" : "Verfügbar"}</span>
        </div>
      </div>
    );
  }

  const openJournal = () => nav(`/monatsjournal?room=${encodeURIComponent(room.id)}`);
  const openReservation = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!availability) return;
    const params = new URLSearchParams({
      new: "1",
      rooms: room.id,
      check_in: availability.check_in,
      check_out: availability.check_out || checkOut,
      guests: String(guests || 1),
    });
    nav(`/buchungen?${params.toString()}`);
  };

  return (
    <div className="relative">
      <div
        role="button" tabIndex={0}
        onClick={selectable ? (e)=>{ e.preventDefault(); onToggle?.(); } : openJournal}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); (selectable ? onToggle : openJournal)?.(); }}}
        className="msb-card overflow-hidden p-0 text-left w-full hover:shadow-lg hover:-translate-y-0.5 transition-all rounded-2xl cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary"
        data-testid={`room-card-${room.id}`}
        title={selectable ? "Zum Reservieren auswählen" : `Alle Buchungen für ${room.name} anzeigen`}>
        <div className="h-48 bg-muted relative">
          <img src={roomImage(room.name)} alt={room.name}
               className="w-full h-full object-cover"
               onError={(e) => { e.currentTarget.src = "https://mainspessartblick.de/wp-content/uploads/go-x/u/3d11a149-efe4-4b07-937d-a9398f8e5448/l0,t89,w2000,h1322/image-1366x903.jpg"; }} />
          {!searchMode && (
            <span className={`status-pill absolute top-3 right-3 ${statusStyle.bg}`} data-testid={`room-status-${room.id}`}>
              {statusStyle.label}
            </span>
          )}
          {selectable && (
            <div className="absolute top-3 left-3 bg-white/95 rounded-lg p-1.5 shadow-md"
                 onClick={(e)=>{ e.stopPropagation(); onToggle?.(); }}
                 data-testid={`room-select-${room.id}`}>
              <Checkbox checked={!!selected} onCheckedChange={()=>onToggle?.()} />
            </div>
          )}
        </div>
        <div className="p-5">
          <div className="flex items-center gap-2">
            <House size={18} weight="duotone" className="text-primary" />
            <h3 className="text-lg font-semibold" style={{ fontFamily: "'Work Sans',sans-serif" }}>{room.name}</h3>
          </div>
          {!searchMode ? (
            isBusy ? (
              <div className="mt-3 text-sm">
                <div className="text-muted-foreground text-xs uppercase tracking-widest">Aktuell belegt</div>
                <div className="font-medium">{room.current_guest_name || "Gast"}</div>
                <div className="text-xs text-muted-foreground mt-0.5">bis {formatDate(room.current_booking.check_out)}</div>
              </div>
            ) : (
              <div className="mt-3 text-sm">
                <div className="text-muted-foreground text-xs uppercase tracking-widest">Aktuell</div>
                <div className="font-medium text-emerald-700">Frei</div>
              </div>
            )
          ) : (
            <div className="mt-3 text-sm">
              <div className="text-muted-foreground text-xs uppercase tracking-widest">Zeitraum verfügbar</div>
              <div className="font-medium text-emerald-700">
                {formatDate(availability.check_in)} → {formatDate(availability.check_out)}
              </div>
              {availability.livingroom_boosted && (
                <div className="mt-1.5 text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                     data-testid={`livingroom-boost-hint-${room.id}`}>
                  Ausnahme: für 2 Personen freigeschaltet
                </div>
              )}
            </div>
          )}
          {!searchMode && room.next_booking && (
            <div className="mt-2 text-sm">
              <div className="text-muted-foreground text-xs uppercase tracking-widest">Nächste Anreise</div>
              <div>{formatDate(room.next_booking.check_in)}</div>
            </div>
          )}
          {availability?.available && !selectable && (
            <Button onClick={openReservation}
                    className="mt-4 w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`reserve-btn-${room.id}`}>
              <CheckCircle size={16} weight="fill"/>
              <span className="ml-1.5">Reservieren · {availability.nights} {availability.nights===1?"Nacht":"Nächte"}</span>
            </Button>
          )}
          {!selectable && (
            <div className="mt-3 text-[11px] text-primary font-semibold uppercase tracking-widest">
              Alle Buchungen ansehen ›
            </div>
          )}
        </div>
      </div>
      {overlay}
    </div>
  );
}
